import { useCallback, useEffect, useState } from "react";
import { ArrowLeft, FileSpreadsheet, LayoutList, Table2 } from "lucide-react";
import { api, download } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { LOCALES } from "../lib/format";
import { Button, Loading, Notice, errorText } from "./ui";

type Cell = { v: string | number | null; t: "text" | "num" | "dec2" | "money" | "date" | "pct"; f?: string };
interface Sheet {
  name: string;
  rows: { cells: Cell[]; bold: boolean; fill: string | null }[];
  headerRows: number;
  truncated: boolean;
}
export interface ViewRequest {
  url: string;
  name: string;
}

/** Dark text on light fills and white on dark ones, whatever the app's own theme. */
function inkFor(hex: string) {
  const n = parseInt(hex.slice(1), 16);
  const lum = (0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255;
  return lum > 0.6 ? "#1d1b16" : "#ffffff";
}

/**
 * Every export, readable right here on any phone — no Excel, Numbers or WPS needed, nothing to download first.
 * It shows the very workbook the download gives (one tab per sheet, the header row stays in view, money in euros),
 * and "Save Excel" keeps the file for later or for sending on.
 */
export function ExportViewer() {
  const { t, lang } = useI18n();
  const [req, setReq] = useState<ViewRequest | null>(null);
  const [data, setData] = useState<{ name: string; sheets: Sheet[] } | null>(null);
  const [tab, setTab] = useState(0);
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  /** Phones read rows best as cards (label: value); the grid is one tap away, and the default on wide screens. */
  const [mode, setMode] = useState<"cards" | "table">(() => (window.innerWidth < 700 ? "cards" : "table"));

  const load = useCallback(
    (r: ViewRequest) => {
      setData(null);
      setErr(null);
      setTab(0);
      api
        .get<{ name: string; sheets: Sheet[] }>(`/api/files/view?path=${encodeURIComponent(r.url)}`)
        .then(setData)
        .catch((e) => setErr(errorText(t, e)));
    },
    [t],
  );

  useEffect(() => {
    const on = (e: Event) => {
      const r = (e as CustomEvent<ViewRequest>).detail;
      setReq(r);
      load(r);
    };
    window.addEventListener("nc:view-export", on);
    return () => window.removeEventListener("nc:view-export", on);
  }, [load]);

  // The phone's back gesture closes the viewer instead of leaving the page.
  useEffect(() => {
    if (!req) return;
    history.pushState({ ...(history.state ?? {}), viewer: true }, "");
    const back = () => setReq(null);
    window.addEventListener("popstate", back);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("popstate", back);
      document.body.style.overflow = "";
    };
  }, [req]);
  const close = () => (history.state?.viewer ? history.back() : setReq(null));

  async function save() {
    if (!req) return;
    setSaving(true);
    try {
      await download(req.url, req.name);
    } catch (e) {
      setErr(errorText(t, e));
    } finally {
      setSaving(false);
    }
  }

  if (!req) return null;
  const sheet = data?.sheets[tab];
  const nf = new Intl.NumberFormat(LOCALES[lang], { maximumFractionDigits: 2 });
  const mf = new Intl.NumberFormat(LOCALES[lang], { style: "currency", currency: "EUR" });
  const d2 = new Intl.NumberFormat(LOCALES[lang], { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const show = (c: Cell) => (c.v === null ? "" : c.t === "money" ? mf.format(Number(c.v)) : c.t === "pct" ? `${nf.format(Number(c.v) * 100)} %` : c.t === "dec2" ? d2.format(Number(c.v)) : c.t === "num" ? nf.format(Number(c.v)) : String(c.v));

  return (
    <div className="xv" role="dialog" aria-modal="true" aria-label={data?.name ?? req.name}>
      <header className="xv-bar">
        <button type="button" className="icon-btn" aria-label={t("common.back")} onClick={close}>
          <ArrowLeft size={22} />
        </button>
        <span className="xv-title">{data?.name ?? req.name}</span>
        <Button className="small" busy={saving} onClick={save}>
          <FileSpreadsheet size={16} /> {t("view.save")}
        </Button>
      </header>
      <div className="xv-mode" role="group" aria-label={t("view.mode")}>
        <button type="button" className={`chip ${mode === "cards" ? "on" : ""}`} aria-pressed={mode === "cards"} onClick={() => setMode("cards")}>
          <LayoutList size={16} /> {t("view.cards")}
        </button>
        <button type="button" className={`chip ${mode === "table" ? "on" : ""}`} aria-pressed={mode === "table"} onClick={() => setMode("table")}>
          <Table2 size={16} /> {t("view.table")}
        </button>
      </div>
      {data && data.sheets.length > 1 && (
        <div className="xv-tabs" role="tablist">
          {data.sheets.map((s, i) => (
            <button key={s.name + i} type="button" role="tab" aria-selected={i === tab} className={`chip ${i === tab ? "on" : ""}`} onClick={() => setTab(i)}>
              {s.name}
            </button>
          ))}
        </div>
      )}
      <div className="xv-body">
        {err ? (
          <Notice tone="bad">
            <div className="stack" style={{ gap: 8 }}>
              <span>{err}</span>
              <button type="button" className="btn small secondary" style={{ alignSelf: "flex-start" }} onClick={() => load(req)}>
                {t("common.retry")}
              </button>
            </div>
          </Notice>
        ) : !sheet ? (
          <Loading />
        ) : sheet.rows.length <= sheet.headerRows ? (
          <p className="muted xv-empty">{t("view.empty")}</p>
        ) : mode === "cards" && sheet.headerRows > 0 ? (
          <Cards sheet={sheet} show={show} />
        ) : (
          <div className="xv-scroll">
            <table className="xv-table">
              <tbody>
                {sheet.rows.map((r, i) => {
                  const head = i < sheet.headerRows;
                  return (
                    <tr key={i} className={head ? "xv-head" : r.bold ? "xv-strong" : ""}>
                      {r.cells.map((c, j) => {
                        const bg = c.f ?? (head ? r.fill : null);
                        return (
                          <td key={j} className={c.t === "text" || c.t === "date" ? "" : "num"} style={bg ? { background: bg, color: inkFor(bg) } : undefined}>
                            {show(c)}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {sheet?.truncated && <p className="small muted">{t("view.truncated")}</p>}
        <p className="small muted xv-hint">{t("view.hint")}</p>
      </div>
    </div>
  );
}

/**
 * One card per row: titles above the header row are shown as headings; the first column(s) name the card, and every
 * other filled cell is a line "label … value", in the header row's words.
 */
function Cards({ sheet, show }: { sheet: Sheet; show: (c: Cell) => string }) {
  const titles = sheet.rows.slice(0, Math.max(0, sheet.headerRows - 1)).filter((r) => r.cells.some((c) => c.v !== null));
  const labels = sheet.rows[sheet.headerRows - 1]?.cells.map((c) => (c.v === null ? "" : String(c.v))) ?? [];
  const data = sheet.rows.slice(sheet.headerRows).filter((r) => r.cells.some((c) => c.v !== null));
  return (
    <div className="xv-cards">
      {titles.map((r, i) => (
        <p key={i} className={i === 0 ? "xv-cards-title" : "small muted"}>
          {r.cells.filter((c) => c.v !== null).map(show).join(" · ")}
        </p>
      ))}
      {data.map((r, i) => {
        const filled = r.cells.map((c, j) => ({ c, j })).filter(({ c }) => c.v !== null && show(c) !== "");
        // A row that starts with a date is named "date · what" (e.g. "2026-10-17 · HEMA").
        const dated = !!filled[0] && (filled[0].c.t === "date" || /^\d{2,4}-\d{2}(-\d{2})?/.test(String(filled[0].c.v)));
        const head = filled.slice(0, dated && filled.length > 2 ? 2 : 1);
        const rest = filled.slice(head.length);
        return (
          <article key={i} className={`card xv-card ${r.bold ? "xv-strong" : ""}`}>
            <strong className="xv-card-head">{head.map(({ c }) => show(c)).join(" · ")}</strong>
            {rest.length > 0 && (
              <dl className="xv-card-list">
                {rest.map(({ c, j }) => (
                  <div key={j} className="xv-card-row">
                    <dt>{labels[j] || "—"}</dt>
                    <dd className={c.t === "text" || c.t === "date" ? "" : "num"} style={c.f ? { background: c.f, color: inkFor(c.f) } : undefined}>
                      {show(c)}
                    </dd>
                  </div>
                ))}
              </dl>
            )}
          </article>
        );
      })}
    </div>
  );
}

/** Show an export on screen (with "Save Excel" there). */
export const viewExport = (url: string, name: string) => window.dispatchEvent(new CustomEvent<ViewRequest>("nc:view-export", { detail: { url, name } }));
